import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import { Form, FormField, Input, ModalDialog, Numeric } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import {
  type InsertScrewFamilyBody,
  type ScrewFamily,
  type UpdateScrewFamilyBody,
} from "../../../../http/beamReinforcement/types/screwFamily";
import { useBRScrewFamiliesApi } from "../../../../http/beamReinforcement/useAdminScrewFamilies";

const MIN_MEASUREMENT = 0.001;

type ScrewFamilyFormBody = {
  name: string | null;
  diameter: number | null;
  headDiameter: number | null;
  yieldMoment: number | null;
  tensileCapacity: number | null;
};

const ScrewFamilyFormDialog: FC<{ screwFamily?: ScrewFamily; onClose: () => void }> = ({ screwFamily, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const screwFamiliesApi = useBRScrewFamiliesApi();
  const t = i18n.translation.beamReinforcementModule;

  const defaultValues: ScrewFamilyFormBody = {
    name: screwFamily?.name ?? null,
    diameter: screwFamily?.diameter ?? null,
    headDiameter: screwFamily?.headDiameter ?? null,
    yieldMoment: screwFamily?.yieldMoment ?? null,
    tensileCapacity: screwFamily?.tensileCapacity ?? null,
  };

  const onSubmit = () => async (formData: ScrewFamilyFormBody) => {
    if (
      formData.name == null ||
      formData.diameter == null ||
      formData.headDiameter == null ||
      formData.yieldMoment == null ||
      formData.tensileCapacity == null
    ) {
      return;
    }

    const body: InsertScrewFamilyBody | UpdateScrewFamilyBody = {
      name: formData.name,
      diameter: formData.diameter,
      headDiameter: formData.headDiameter,
      yieldMoment: formData.yieldMoment,
      tensileCapacity: formData.tensileCapacity,
    };

    try {
      if (screwFamily) {
        await screwFamiliesApi.updateBRScrewFamily(screwFamily.id, body);
      } else {
        await screwFamiliesApi.insertBRScrewFamily(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        screwFamily
          ? `${i18n.translation.common.edit} ${t.screwFamilies.singular}`
          : `${i18n.translation.common.add} ${t.screwFamilies.singular}`
      }
    >
      <Form<ScrewFamilyFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
      >
        {({ control, register, formState: { errors }, reset }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [screwFamily]);

          return (
            <FormFieldsContainer fullWidth>
              <FormField label={i18n.translation.common.name} error={errors.name}>
                {({ labelId, isInvalid }) => (
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    {...register("name", {
                      required: true,
                      minLength: 1,
                    })}
                  />
                )}
              </FormField>
              <Controller
                name="diameter"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.screwDiameter} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="headDiameter"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screws.fields.headDiameter} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT}
                        decimalScale={2}
                        suffix={" " + i18n.translation.common.unitMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="yieldMoment"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screws.fields.yieldMoment} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitNMm}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
              <Controller
                name="tensileCapacity"
                control={control}
                rules={{ required: true, min: MIN_MEASUREMENT }}
                render={({ field, fieldState }) => (
                  <FormField label={t.screws.fields.tensileCapacity} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Numeric
                        id={labelId}
                        value={field.value ?? undefined}
                        min={MIN_MEASUREMENT}
                        decimalScale={0}
                        suffix={" " + i18n.translation.common.unitN}
                        allowNegative={false}
                        isInvalid={isInvalid}
                        onValueChange={(value) => field.onChange(value)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default ScrewFamilyFormDialog;
